import type { Scope } from '@sentry/browser';
import { addBreadcrumb, readContexts, setContext } from './scope';
import { sentryConfig } from './config';
import { dispatch, startClient } from './lazyClient';
import type { SdkName, SdkSentry, Tags, TagValue } from './types';
import {
  appendToTopException,
  definedTags,
  markFirstCapture,
  flattenKeys,
  withHostMarkerHidden,
} from './utils';

/** One decision per page load, shared by every SDK facade of the bundle. */
const logsSampled =
  sentryConfig.logsEnabled && Math.random() < sentryConfig.logsSampleRate;

/** Creates the Sentry facade of one SDK; captures are inert until `init`. */
function createSdkSentry(sdk: SdkName): SdkSentry {
  const tags: Tags = { sdk };
  let userId = 'not-set';
  let active = false;

  const setTag = (key: string, value: TagValue) => {
    if (value === undefined) delete tags[key];
    else tags[key] = value;
  };

  return {
    init(gateway, extraTags = {}) {
      try {
        if (!sentryConfig.enabled) return;
        setTag('gateway', gateway);
        Object.keys(extraTags).forEach((key) => setTag(key, extraTags[key]));
        active = true;
        startClient();
      } catch (e) {
        // Sentry must never break the SDK
      }
    },

    setTag(key, value) {
      try {
        setTag(key, value);
      } catch (e) {
        // Sentry must never break the SDK
      }
    },

    captureException(error, context = {}) {
      try {
        if (!active || !markFirstCapture(error)) return;
        const { transaction, detail, extra, fingerprint, level } = context;
        const eventTags = definedTags({ ...tags, ...context.tags });
        const contexts = readContexts();

        // Sentry hands this callback a clone of the target scope made for this one event,
        // so nothing set here leaks onto the shared scope
        const captureContext = (scope: Scope): Scope => {
          scope.update({ tags: eventTags, contexts, extra, fingerprint, level });
          scope.setUser({ id: userId });
          if (transaction) scope.setTransactionName(transaction);
          if (detail) {
            scope.addEventProcessor((event) =>
              appendToTopException(event, detail),
            );
          }
          return scope;
        };
        // marker handled at capture time: a partner may mark the error while it is queued
        dispatch(({ scope: target }) => {
          withHostMarkerHidden(error, () => {
            target.captureException(error, { captureContext });
          });
        });
      } catch (e) {
        // Sentry must never break the SDK
      }
    },

    captureMessage(message, level = 'info') {
      try {
        if (!active) return;
        const captureContext = {
          tags: { ...tags },
          contexts: readContexts(),
        };
        dispatch(({ scope: target }) => {
          target.captureMessage(message, level, { captureContext });
        });
      } catch (e) {
        // Sentry must never break the SDK
      }
    },

    setUser(id) {
      userId = id;
    },

    log(level, message, data = {}) {
      try {
        if (!active || !logsSampled) return;
        const logAttributes = definedTags({
          ...tags,
          'user.id': userId,
          ...flattenKeys('data', data),
        });
        dispatch(({ captureLog }) => {
          captureLog(level, message, logAttributes);
        });
      } catch (e) {
        // Sentry must never break the SDK
      }
    },
  };
}

/**
 * Every Sentry op of the bundle: shared-scope ops (`addBreadcrumb`, `setContext`) at the top,
 * per-SDK facades under `scdk` / `loan` (each initialised by its SDK's constructor).
 */
export const sentry = {
  addBreadcrumb,
  setContext,
  scdk: createSdkSentry('scDK'),
  loan: createSdkSentry('ScLoan'),
};

export type {
  LogAttributes,
  LogData,
  LogLevel,
  SdkName,
  SdkSentry,
  SentryBreadcrumb,
  SentryCaptureContext,
} from './types';

export { CrumbCategory } from './types';
export { flattenKeys } from './utils';